import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import Alert from 'react-bootstrap/Alert'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'

import EventDetailsContext from '../../context/EventDetails/EventDetails'
import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'

import {
  ORGANIZATION_PERMISSION_SLUGS,
  resaleMinimumErrorMessage,
  ticketLimitErrorMessage,
} from '@blocktickets/shared-utils'

import {
  addDetailsToEvent,
  createAttractions,
  getEvent,
  removeAttraction,
  updateAttraction,
  upload,
} from '../../utilities/api'
import {
  b64toBlob,
  checkPermission,
  isEventPast,
  scrollToTop,
  sortAttractions,
} from '../../utilities/helpers'
import AuthService from '../../utilities/services/auth.service'
import useSafeAsync from '../../utilities/useSafeAsync'

import { CreateEventButtons } from '../CreateEventButtons'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { AddAttraction } from './AddAttraction'
import { Description } from './Description'
import { PromotionalDescription } from './PromotionalDescription'
import { EventImage } from './EventImage'
import { Fees } from './Fees'
import { ResalePricePoints } from './ResalePricePoints'
import { TicketLimit } from './TicketLimit'
import { WaitingRoomSettings } from './WaitingRoomSettings'
import { InvoiceEmailNote } from '../InvoiceEmailNote'

export default function EventSettingsWrapper({ eventId }) {
  const safeSetState = useSafeAsync()
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const {
    updateEvent,
    updateCanPublish,
    updateIsEventPublishing,
    updateIsEventPublished,
    updateIsEventOnsale,
    updateHasEventEnded,
    updateIsEventSoldout,
  } = useContext(EventDetailsContext)

  const navigate = useNavigate()

  const { orgPermissions } = useContext(UserContext)

  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT
    )

  // event image
  const [image, setImage] = useState()

  const [attractions, setAttractions] = useState([])

  // used for flag if custom amount
  const [enableResale, setEnableResale] = useState(false)

  const [hasMinimumResale, setHasMinimumResale] = useState(null)

  // seperate state because I have to manipulate it based on hasMinimumResale state
  const [minimumResaleAmount, setMinimumResaleAmount] = useState('')

  // used for flag if custom amount
  const [hasTicketLimit, setHasTicketLimit] = useState(null)

  // seperate state because I have to manipulate it based on hasTicketLimit state
  const [ticketLimit, setTicketLimit] = useState('')

  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(false)
  const [waitingRoomMaxConcurrent, setWaitingRoomMaxConcurrent] = useState('75')
  const [waitingRoomAdmissionTtlMinutes, setWaitingRoomAdmissionTtlMinutes] =
    useState('15')
  const [waitingRoomFeatureEnabled, setWaitingRoomFeatureEnabled] =
    useState(false)

  // decouple description from event summary
  const [description, setDescription] = useState('')

  const [promotionalDescription, setPromotionalDescription] = useState('')

  const [invoiceEmailNote, setInvoiceEmailNote] = useState('')

  const [initialState, setInitialState] = useState(false)

  const [event, setEvent] = useState()

  const [errors, setErrors] = useState([])

  const [alert, setAlert] = useState({
    show: false,
    variant: '',
    message: '',
  })

  const [showFooter, setShowFooter] = useState(false)

  const [isSaving, setIsSaving] = useState(false)
  const isSavingRef = useRef(false)

  const [hasRequiredFieldError, setHasRequiredFieldError] = useState(false)

  const [requiredFieldErrorStatus, setRequiredFieldErrorStatus] = useState({})

  const [attractionsToUpdate, setAttractionsToUpdate] = useState([])

  const [attractionIdsToRemove, setAttractionIdsToRemove] = useState([])

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }

    showLoading()
    getEvent(eventId)
      .then((res) => {
        if (res.data) {
          safeSetState(() => {
            setEvent(res.data)
            updateEvent(res.data)
            updateIsEventOnsale(res?.data)
            updateHasEventEnded(res.data)
            updateIsEventSoldout(res.data)
            updateIsEventPublished(res?.data)
            updateIsEventPublishing(res.data)
            setImage(res?.data?.image?.url)
            if (res.data?.attractions) setAttractions(res.data?.attractions)
            setDescription(res?.data?.summary || '')
            setPromotionalDescription(
              res?.data?.promotionalDescriptionText || ''
            )
            setInvoiceEmailNote(res?.data?.invoiceEmailNote || '')
            // only do this if has image - means editing existing event
            const isEditing = res?.data?.image?.id
            setEnableResale(Boolean(res?.data?.enableResale))
            if (isEditing) {
              setHasMinimumResale(res?.data?.resaleEnableMinimum)
              setMinimumResaleAmount(res?.data?.resaleMinimumPercentage || '')
              setHasTicketLimit(res?.data?.ticketLimit)
              setTicketLimit(res?.data?.globalTicketLimit || '')
            }

            setWaitingRoomFeatureEnabled(
              Boolean(res?.data?.waitingRoomFeatureEnabled)
            )
            setWaitingRoomEnabled(Boolean(res?.data?.waitingRoomEnabled))
            setWaitingRoomMaxConcurrent(
              String(res?.data?.waitingRoomMaxConcurrent || 75)
            )
            setWaitingRoomAdmissionTtlMinutes(
              String(res?.data?.waitingRoomAdmissionTtlMinutes || 15)
            )

            // save initial state to check whether to show save buttons
            saveInitialState(
              res?.data,
              res?.data?.image?.url,
              res?.data?.attractions,
              res?.data?.summary || '',
              res?.data?.promotionalDescriptionText || '',
              isEditing ? res?.data?.resaleEnableMinimum : null,
              res?.data?.resaleMinimumPercentage || '',
              isEditing ? res?.data?.ticketLimit : null,
              res?.data?.globalTicketLimit || '',
              Boolean(res?.data?.waitingRoomEnabled),
              String(res?.data?.waitingRoomMaxConcurrent || 75),
              String(res?.data?.waitingRoomAdmissionTtlMinutes || 15),
              res?.data?.invoiceEmailNote || ''
            )
            hideLoading()
          })
        }
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
  }, [eventId, orgPermissionsReady, hasPermission])

  useEffect(() => {
    // Listens for image upload
  }, [attractions])

  // show/hide footer
  useEffect(() => {
    setShowFooter(
      initialState?.image !== image ||
        !isEqual(initialState?.attractions, attractions) ||
        initialState?.description !== description ||
        (initialState?.promotionalDescription || '') !==
          (promotionalDescription || '') ||
        parseFloat(initialState?.event?.facilityFees) !==
          parseFloat(event?.facilityFees) ||
        initialState?.enableResale !== enableResale ||
        initialState?.hasMinimumResale !== hasMinimumResale ||
        (hasMinimumResale &&
          parseFloat(initialState?.minimumResaleAmount) !==
            parseFloat(minimumResaleAmount)) ||
        initialState?.hasTicketLimit !== hasTicketLimit ||
        (hasTicketLimit &&
          parseFloat(initialState?.ticketLimit) !== parseFloat(ticketLimit)) ||
        (waitingRoomFeatureEnabled &&
          (Boolean(initialState?.waitingRoomEnabled) !==
            Boolean(waitingRoomEnabled) ||
            String(initialState?.waitingRoomMaxConcurrent || '') !==
              String(waitingRoomMaxConcurrent || '') ||
            String(initialState?.waitingRoomAdmissionTtlMinutes || '') !==
              String(waitingRoomAdmissionTtlMinutes || ''))) ||
        (initialState?.invoiceEmailNote || '') !== (invoiceEmailNote || '')
    )
  }, [
    initialState,
    image,
    event?.facilityFees,
    attractions,
    description,
    promotionalDescription,
    enableResale,
    hasMinimumResale,
    minimumResaleAmount,
    hasTicketLimit,
    ticketLimit,
    waitingRoomFeatureEnabled,
    waitingRoomEnabled,
    waitingRoomMaxConcurrent,
    waitingRoomAdmissionTtlMinutes,
    invoiceEmailNote,
  ])

  useEffect(() => {
    if (hasRequiredFieldError) {
      updateRequiredFieldErrorStatus()
    }
  }, [
    image,
    attractions,
    event?.facilityFees,
    hasMinimumResale,
    minimumResaleAmount,
    hasTicketLimit,
    ticketLimit,
  ])

  useEffect(() => {
    if (hasRequiredFieldError) {
      // if all fields are filled in: dismiss alert
      if (
        Object.keys(requiredFieldErrorStatus).every(
          (key) => !requiredFieldErrorStatus[key]
        )
      ) {
        closeAlert()
        setHasRequiredFieldError(false)
        setRequiredFieldErrorStatus({})
      }
    }
  }, [hasRequiredFieldError, requiredFieldErrorStatus])

  useEffect(() => {
    // close all alerts when fields are edited and there is no required fields error or input errors
    if (alert.show && errors?.length === 0 && !hasRequiredFieldError) {
      closeAlert()
    }
  }, [
    image,
    event?.facilityFees,
    attractions,
    description,
    promotionalDescription,
    hasMinimumResale,
    minimumResaleAmount,
    hasTicketLimit,
    ticketLimit,
    waitingRoomEnabled,
    waitingRoomMaxConcurrent,
    waitingRoomAdmissionTtlMinutes,
  ])

  // remove errors for inputs when inputs change
  useEffect(() => {
    if (errors) {
      const el = findError('minimumResale')
      if (el) setErrors(errors.filter((error) => error.field !== el.field))
    }
  }, [minimumResaleAmount])

  useEffect(() => {
    if (errors) {
      const el = findError('ticketLimit')
      if (el) setErrors(errors.filter((error) => error.field !== el.field))
    }
  }, [ticketLimit])

  useEffect(() => {
    if (errors) {
      setErrors(
        errors.filter(
          (error) =>
            error.field !== 'waitingRoomMaxConcurrent' &&
            error.field !== 'waitingRoomAdmissionTtlMinutes'
        )
      )
    }
  }, [waitingRoomMaxConcurrent, waitingRoomAdmissionTtlMinutes])

  // find error in errors array
  const findError = (field) => {
    return errors.find((error) => error.field === field)
  }

  const updateRequiredFieldErrorStatus = () => {
    setRequiredFieldErrorStatus({
      image: !image,
      attractions: attractions?.length === 0,
      fees: isNaN(parseFloat(event?.facilityFees)),
      resale: !isValidMinimumResale(),
      limit: !isValidTicketLimit(),
    })
  }

  const closeAlert = () => {
    setAlert({ show: false, variant: '', message: '' })
  }

  const isEditable = () => {
    return isEventPast(event)
  }

  const handleChange = (e) => {
    if (e) setEvent({ ...event, [e.target.name]: e.target.value })
  }

  const handleValid = (e) => {
    const { name } = e.target

    switch (name) {
      case 'minimumResale':
        {
          const message = resaleMinimumErrorMessage(minimumResaleAmount)
          if (message) {
            setErrors([
              ...errors,
              {
                field: name,
                message,
              },
            ])
          }
        }
        break

      case 'ticketLimit':
        {
          const message = ticketLimitErrorMessage(ticketLimit)
          if (message) {
            setErrors([
              ...errors,
              {
                field: name,
                message,
              },
            ])
          }
        }
        break
      case 'waitingRoomMaxConcurrent':
        if (
          waitingRoomEnabled &&
          (!waitingRoomMaxConcurrent ||
            parseInt(waitingRoomMaxConcurrent, 10) < 1)
        ) {
          setErrors([
            ...errors,
            {
              field: name,
              message: 'Max concurrent shoppers must be at least 1',
            },
          ])
        }
        break
      case 'waitingRoomAdmissionTtlMinutes':
        if (
          waitingRoomEnabled &&
          (!waitingRoomAdmissionTtlMinutes ||
            parseInt(waitingRoomAdmissionTtlMinutes, 10) < 1)
        ) {
          setErrors([
            ...errors,
            {
              field: name,
              message: 'Admission session length must be at least 1 minute',
            },
          ])
        }
        break
      default:
        break
    }
  }

  const saveInitialState = (
    event,
    image,
    attractions,
    description,
    promotionalDescriptionValue = '',
    hasMinimumResale,
    minimumResaleAmount,
    hasTicketLimit,
    ticketLimit,
    waitingRoomEnabledValue = false,
    waitingRoomMaxConcurrentValue = '75',
    waitingRoomAdmissionTtlMinutesValue = '15',
    invoiceEmailNoteValue = ''
  ) => {
    // save initial state to check whether to show save buttons
    setInitialState({
      event,
      image,
      attractions,
      description,
      promotionalDescription: promotionalDescriptionValue,
      enableResale: Boolean(event?.enableResale),
      hasMinimumResale,
      minimumResaleAmount,
      hasTicketLimit,
      ticketLimit,
      waitingRoomEnabled: waitingRoomEnabledValue,
      waitingRoomMaxConcurrent: waitingRoomMaxConcurrentValue,
      waitingRoomAdmissionTtlMinutes: waitingRoomAdmissionTtlMinutesValue,
      invoiceEmailNote: invoiceEmailNoteValue,
    })
  }

  const isValid = (hasField, field) => {
    // not null and is false or true and string is not empty
    return hasField !== null && (!hasField || (hasField && field !== ''))
  }
  const isValidTicketLimit = () => {
    return isValid(hasTicketLimit, ticketLimit)
  }

  const isValidMinimumResale = () => {
    return isValid(hasMinimumResale, minimumResaleAmount)
  }

  const handleSave = (e) => {
    e?.preventDefault?.()
    // if not showing footer, meaning nothing has changed, don't save
    if (!showFooter) return
    // Prevent double-submit (button disabled state updates async)
    if (isSavingRef.current) return

    // if error, don't save
    if (hasRequiredFieldError) {
      scrollToTop()
      return
    }
    const hasError =
      !image ||
      attractions?.length === 0 ||
      isNaN(parseFloat(event?.facilityFees)) ||
      !isValidTicketLimit() ||
      !isValidMinimumResale()

    if (hasError) {
      updateRequiredFieldErrorStatus()
      setHasRequiredFieldError(hasError)
      scrollToTop()
      setAlert({
        show: true,
        variant: 'danger',
        message:
          'You are missing required subject fields. Please fill out all required fields before continuing.',
      })
      return
    }

    // if other errors
    if (errors?.length > 0) {
      scrollToTop()
      setAlert({
        show: true,
        variant: 'danger',
        message:
          'You have input errors. Please correct all input errors before continuing.',
      })
      return
    }

    isSavingRef.current = true
    setIsSaving(true)

    const attractionIdsBeingReplaced = new Set(attractionIdsToRemove || [])

    const buildAttractionCreates = () => {
      const creates = []

      attractions.forEach((attraction) => {
        // Only create brand-new rows or rows whose artwork is being replaced.
        // Do not infer "new" from missing DB ids — temp client ids used to cause
        // re-creates on every subsequent save while local event state was stale.
        const shouldCreate =
          attraction.isNew === true ||
          attractionIdsBeingReplaced.has(attraction.id)
        if (!shouldCreate) return

        const artworkUrl = attraction.artwork?.url
        if (!artworkUrl) return

        // Reuse existing Strapi media by id (from attraction autocomplete)
        if (
          String(artworkUrl).startsWith('https://') &&
          attraction.artwork?.id
        ) {
          creates.push(
            createAttractions({
              data: {
                primary: attraction.primary,
                name: attraction.name,
                order: attraction.order,
                eventUUID: eventId,
                artwork: attraction.artwork.id,
              },
            })
          )
          return
        }

        // Skip already-persisted https artwork without a media id
        if (String(artworkUrl).startsWith('https://')) return

        let attractionData = {}
        const block = artworkUrl.split(';')
        const contentType = block[0].split(':')[1]
        const realData = block[1].split(',')[1]
        const formData = new FormData()
        const blob = b64toBlob(realData, contentType)
        formData.append(`files.artwork`, blob)
        attractionData['primary'] = attraction.primary
        attractionData['name'] = attraction.name
        attractionData['order'] = attraction.order
        attractionData['eventUUID'] = eventId
        formData.append('data', JSON.stringify(attractionData))
        creates.push(createAttractions(formData))
      })

      return creates
    }

    const persistAttractionUpdatesAndDeletes = async () => {
      if (attractionIdsToRemove?.length > 0) {
        await Promise.all(
          attractionIdsToRemove.map((id) => removeAttraction(id))
        )
      }

      if (attractionsToUpdate?.length > 0) {
        await Promise.all(
          attractionsToUpdate
            .filter(
              (attraction) =>
                !attractionIdsToRemove.some((id) => attraction.id === id)
            )
            .map((attraction) =>
              updateAttraction({
                data: {
                  id: attraction.id,
                  name: attraction.name,
                  primary: attraction.primary,
                  order: attraction.order,
                },
              })
            )
        )
      }
    }

    const finishSaveSuccess = async ({
      nextImage,
      eventPayload,
      navigateToSeatmap = false,
    }) => {
      const refreshed = await refreshEventState()
      const savedAttractions = refreshed?.attractions || []

      if (nextImage) setImage(nextImage)

      saveInitialState(
        refreshed || event,
        nextImage || image,
        savedAttractions,
        description,
        promotionalDescription,
        hasMinimumResale,
        minimumResaleAmount,
        hasTicketLimit,
        ticketLimit,
        waitingRoomEnabled,
        waitingRoomMaxConcurrent,
        waitingRoomAdmissionTtlMinutes,
        invoiceEmailNote
      )

      if (eventPayload) {
        updateCanPublish(eventPayload?.status, eventPayload?.image?.id)
        updateIsEventOnsale(eventPayload)
        updateHasEventEnded(eventPayload)
        updateIsEventSoldout(eventPayload)
        updateIsEventPublished(eventPayload)
        updateIsEventPublishing(eventPayload)
      }

      setAttractionIdsToRemove([])
      setAttractionsToUpdate([])
      isSavingRef.current = false
      setIsSaving(false)
      scrollToTop()
      setAlert({
        show: true,
        variant: 'success',
        message: 'Your details have been updated.',
      })

      if (navigateToSeatmap) {
        navigate(`/event/${eventId}/seatmap`)
      }
    }

    const finishSaveError = (err) => {
      console.error(err)
      isSavingRef.current = false
      setIsSaving(false)
      scrollToTop()
      setAlert({
        show: true,
        variant: 'danger',
        message: 'Unable to save details. Please try again.',
      })
    }

    let data = {}
    data['description'] = description || null
    data['promotionalDescription'] = promotionalDescription || ''
    data['eventUUID'] = eventId
    data['facilityFees'] = parseFloat(event?.facilityFees)
    data['enableResale'] = Boolean(enableResale)
    data['resaleEnableMinimum'] = hasMinimumResale
    data['resaleMinimumPercentage'] = hasMinimumResale
      ? minimumResaleAmount
      : undefined
    data['ticketLimit'] = hasTicketLimit
    data['globalTicketLimit'] = ticketLimit != '' ? ticketLimit : null
    data['invoiceEmailNote'] = invoiceEmailNote || ''
    if (waitingRoomFeatureEnabled) {
      data['waitingRoomEnabled'] = Boolean(waitingRoomEnabled)
      data['waitingRoomMaxConcurrent'] = waitingRoomEnabled
        ? Math.max(1, parseInt(waitingRoomMaxConcurrent, 10) || 75)
        : 75
      data['waitingRoomAdmissionTtlMinutes'] = waitingRoomEnabled
        ? Math.max(1, parseInt(waitingRoomAdmissionTtlMinutes, 10) || 15)
        : 15
    }

    const attractionCreates = buildAttractionCreates()
    const hadNoAttractions = (event?.attractions || []).length === 0
    const hadNoOffers = (event?.offers || []).length === 0

    // add main event image to event
    if (image && !image.startsWith('https://')) {
      const formData = new FormData()
      const ImageURL = image
      const block = ImageURL.split(';')
      const contentType = block[0].split(':')[1]
      const realData = block[1].split(',')[1]
      const blob = b64toBlob(realData, contentType)
      formData.append(`files`, blob)

      Promise.all([
        ...attractionCreates,
        upload(formData).then((res) => {
          data['image'] = res?.data[0]?.id
          return addDetailsToEvent({ data })
        }),
      ])
        .then(async (results) => {
          const detailsRes = results[results.length - 1]
          await persistAttractionUpdatesAndDeletes()
          await finishSaveSuccess({
            nextImage: detailsRes?.data?.image?.url,
            eventPayload: detailsRes?.data,
            navigateToSeatmap: hadNoAttractions && hadNoOffers,
          })
        })
        .catch(finishSaveError)
    } else {
      data['image'] = event?.image?.id

      Promise.all([...attractionCreates, addDetailsToEvent({ data })])
        .then(async () => {
          await persistAttractionUpdatesAndDeletes()
          await finishSaveSuccess({
            nextImage: image,
            eventPayload: null,
          })
        })
        .catch(finishSaveError)
    }
  }

  const refreshEventState = async () => {
    try {
      const res = await getEvent(eventId)
      const data = res?.data
      if (!data) return null

      setEvent(data)
      setAttractions(data.attractions || [])
      updateEvent(data)
      return data
    } catch (err) {
      console.error(err)
      return null
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <section
        data-testid="event-settings-form-section"
        className="wrapper event-form"
      >
        {alert.show && (
          <>
            <Alert variant={alert.variant} className="mb-5">
              <p>{alert.message}</p>
            </Alert>
          </>
        )}
        <header className="section-header section-heading section-heading--secondary">
          <h1>Event settings</h1>
        </header>
        <Form onSubmit={handleSave}>
          <Card body className="card--md">
            <EventImage
              eventName={event?.name}
              image={image}
              setImage={setImage}
              isDisabled={isEditable()}
              requiredFieldErrorStatus={requiredFieldErrorStatus}
            />
          </Card>
          <Card body className="card--md">
            <AddAttraction
              event={event}
              attractions={sortAttractions(attractions)}
              setAttractions={setAttractions}
              isDisabled={isEditable()}
              requiredFieldErrorStatus={requiredFieldErrorStatus}
              toUpdate={attractionsToUpdate}
              setToUpdate={setAttractionsToUpdate}
              idsToRemove={attractionIdsToRemove}
              setIdsToRemove={setAttractionIdsToRemove}
            />
          </Card>
          <Card body className="card--md">
            <Description
              description={description}
              setDescription={setDescription}
              isDisabled={isEditable()}
            />
          </Card>
          <Card body className="card--md">
            <PromotionalDescription
              value={promotionalDescription}
              onChange={(e) => setPromotionalDescription(e.target.value)}
              isDisabled={isEditable()}
            />
          </Card>
          <Card body className="card--md">
            <InvoiceEmailNote
              value={invoiceEmailNote}
              onChange={(e) => setInvoiceEmailNote(e.target.value)}
              subjectLabel="event"
            />
          </Card>
          <Card body className="card--md">
            <Fees
              event={event}
              handleChange={handleChange}
              isDisabled={isEditable()}
              requiredFieldErrorStatus={requiredFieldErrorStatus}
            />
          </Card>
          <Card body className="card--md">
            <ResalePricePoints
              enableResale={enableResale}
              setEnableResale={setEnableResale}
              hasMinimumResale={hasMinimumResale}
              setHasMinimumResale={setHasMinimumResale}
              minimumResaleAmount={minimumResaleAmount}
              setMinimumResaleAmount={setMinimumResaleAmount}
              isDisabled={isEditable()}
              errors={errors}
              requiredFieldErrorStatus={requiredFieldErrorStatus}
              handleValid={handleValid}
              findError={findError}
            />
          </Card>
          <Card body className="card--md">
            <TicketLimit
              ticketLimit={ticketLimit}
              setTicketLimit={setTicketLimit}
              hasTicketLimit={hasTicketLimit}
              setHasTicketLimit={setHasTicketLimit}
              errors={errors}
              isDisabled={isEditable()}
              requiredFieldErrorStatus={requiredFieldErrorStatus}
              handleValid={handleValid}
              findError={findError}
            />
          </Card>
          {waitingRoomFeatureEnabled && (
            <Card body className="card--md">
              <WaitingRoomSettings
                waitingRoomEnabled={waitingRoomEnabled}
                setWaitingRoomEnabled={setWaitingRoomEnabled}
                waitingRoomMaxConcurrent={waitingRoomMaxConcurrent}
                setWaitingRoomMaxConcurrent={setWaitingRoomMaxConcurrent}
                waitingRoomAdmissionTtlMinutes={waitingRoomAdmissionTtlMinutes}
                setWaitingRoomAdmissionTtlMinutes={
                  setWaitingRoomAdmissionTtlMinutes
                }
                isDisabled={isEditable()}
              />
            </Card>
          )}
        </Form>
      </section>
      {showFooter && (
        <CreateEventButtons
          isEditing={event?.image?.id || event?.offers?.length > 0}
          isSaving={isSaving}
          handleSave={handleSave}
        />
      )}
    </>
  )
}
